/**
 * The decision-tree canvas.
 *
 * React Flow (`@xyflow/react`) in fully CONTROLLED mode: nodes and edges are derived
 * from the draft definition on every render by `projectQuestionnaire`, and dragging is
 * disabled. That is not a limitation we settled for — it is the design:
 *
 *  - Coordinates come from the projection, so two renders of the same rules line up
 *    pixel-for-pixel. The surface's headline job is "diff the draft against what is
 *    live", and an auto-layout (or a human nudging boxes) makes that comparison
 *    unreadable.
 *  - A node's position therefore carries information — rank = how far into the walk,
 *    column = which branch — instead of being wherever someone last dropped it.
 *  - There is nowhere to persist a position anyway: the stored record is a
 *    `QuestionnaireNode`, which has no x/y. A draggable canvas would invent state the
 *    backend cannot keep.
 *
 * Pan, zoom, fit-to-view, minimap and selection are all still on, which is what people
 * actually use a canvas for on a 15-node tree.
 */
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  type EdgeTypes,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import ConditionEdge from './ConditionEdge';
import SkipEdge from './SkipEdge';

import type { CpaNode, Projection } from '../../utils/projection';
import {
  CARD_TITLE_MAX_LINES,
  COL_GAP,
  GRANT_W,
  NODE_H,
  NODE_W,
  type QuestionKind,
  ROW_GAP,
  cardHeight,
} from '../../utils/projection';
import { planDrop } from '../../utils/dropPlacement';
import { uiComponentLabel } from '../../utils/uiComponents';
import './TreeCanvas.css';

/** Payload React Flow carries for us. Mirrors `CpaNode['data']` plus render flags. */
type QuestionData = {
  cpa: CpaNode;
  isEntry: boolean;
  worstSeverity: 'error' | 'warning' | null;
  /** Mirrors the canvas prop so the node can show its handles. */
  connectable: boolean;
  isNew?: boolean;
  /** How many edges leave this card — one source port each. */
  outCount: number;
};

/**
 * A question node — the only kind that corresponds to a stored record.
 *
 * Badges exist to make the three things an editor most often gets wrong visible
 * without opening the inspector: which id the expressions use (`qualifiedNodeId`, NOT
 * `nodeId`), whether the answer is converted to a number, and how many eligibility
 * rules hang off it.
 */
function QuestionNodeView({ data, selected }: NodeProps) {
  const { cpa, worstSeverity, connectable, isNew, outCount } =
    data as unknown as QuestionData;
  const handleClass = connectable ? 'cpa-handle cpa-handle--live' : 'cpa-handle';
  const n = cpa.data.node!;
  return (
    <div
      className={[
        'cpa-node',
        'cpa-node--question',
        selected ? 'cpa-node--selected' : '',
        isNew ? 'cpa-node--new' : '',
        worstSeverity ? `cpa-node--${worstSeverity}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
      /* `height`, not `minHeight`: the projection reserved exactly this much and the
         gap below it belongs to the connector. A card free to grow past it is what
         made the edges invisible. */
      style={{ width: cpa.width, height: cpa.height }}
    >
      <Handle type="target" position={Position.Top} className={handleClass} />
      {/*
        * The answer KIND. One short token, not a row of badges — it says what this
        * card is, which is the one thing a reader cannot infer from the question text.
        * Only four exist because only four are in the model; see `QuestionKind`.
        */}
      <span className={`cpa-kind cpa-kind--${cpa.data.questionKind}`}>
        {KIND_LABEL[cpa.data.questionKind ?? 'text']}
        {/* The unit rides on the kind rather than being a kind of its own — an amount
            is a number with a label. */}
        {n.unit ? ` · ${n.unit}` : ''}
        {n.uiComponent ? ` · ${uiComponentLabel(n.uiComponent)}` : ''}
      </span>
      {/*
        * THE QUESTION, and nothing else — the mock's card is a single piece of text.
        *
        * The id row (nodeId + 入口/终点/多选/dataType) and the meta row
        * (qualifiedNodeId + 资格 N + 自动 N) used to be here. Every one of those is
        * still reachable: the inspector shows all of them on click, and the static
        * checker still flags the two that silently break rules (a missing
        * `qualifiedNodeId`, a numeric comparison on a TEXT node) — so dropping them
        * from the card costs visibility, not safety.
        *
        * What stays on the card is STATE rather than content: the severity edge, the
        * selection ring, the just-created mark. Those answer "is something wrong
        * here" without being read.
        */}
      <div className="cpa-node__label">问题</div>
      {/* The clamp comes from the projection's own constant rather than a number in the
          CSS, so the lines the card draws and the lines the layout reserved cannot
          disagree. */}
      <div
        className="cpa-node__title"
        style={{ WebkitLineClamp: CARD_TITLE_MAX_LINES }}
        title={n.questionText.cn || n.questionText.en}
      >
        {n.questionText.cn || n.questionText.en}
      </div>

      {/*
        * The options, under the question, behind a divider and their own label.
        *
        * 「选项」 rather than 「答案」: these are what the question OFFERS, and the answer
        * is whichever one the BD picks at runtime. The card shows the offer, and calling
        * it an answer invites reading a card as though it already held a response.
        *
        * A decision tree is unreadable without them: a card saying only 「这是迁移项目吗？」
        * leaves the reader to click it to find out whether the branches are 是/否 or
        * something else, while the edge labels name options the card never showed. But
        * stacked bare under the question they read as a continuation of it — hence the
        * 「问题」/「选项」 labels and the rule between them.
        *
        * Rendered only for the kinds that HAVE options. Every one is listed: the row cap
        * is high enough that no live question reaches it, because a card that shows a
        * prefix of its options is worse than a tall card — an edit to a hidden option
        * then looks like the canvas failing to update.
        *
        * SLICED to `shownOptions` rather than drawn in full behind a clip. The clip was
        * doing the hiding, which put the "+N" chip after every chip in the flex flow —
        * on the last row of the full set, not the reserved one — so the hint was itself
        * below the cut. Slicing makes the DOM match what `cardSize` reserved, and the
        * card's own `overflow: hidden` goes back to being only a backstop.
        */}
      {cpa.optionRows > 0 && (
        <div className="cpa-node__options">
          <div className="cpa-node__label">选项</div>
          <div className="cpa-node__opts">
            {(n.answerOptions ?? []).slice(0, cpa.shownOptions).map((o) => (
              <span key={o.optionId} className="cpa-node__opt" title={`value = ${o.value}`}>
                {o.optionText.cn || o.optionText.en || o.value}
              </span>
            ))}
            {cpa.hiddenOptions > 0 && (
              <span className="cpa-node__opt cpa-node__opt--more">+{cpa.hiddenOptions}</span>
            )}
          </div>
        </div>
      )}
      {/*
        * One source port per out-edge, spread across the bottom edge.
        *
        * There used to be a single handle at the centre, so every out-edge left from
        * the SAME point: Q0.2's five purpose branches overlapped into one thick stub
        * before fanning out, and which line was which was unreadable near the card.
        *
        * `max(outCount, 1)` because a node with no out-edge still needs somewhere to
        * start dragging a new connection from.
        */}
      {Array.from({ length: Math.max(outCount, 1) }, (_, i) => (
        <Handle
          key={i}
          type="source"
          id={`s-${i}`}
          position={Position.Bottom}
          className={handleClass}
          style={{ left: `${((i + 1) / (Math.max(outCount, 1) + 1)) * 100}%` }}
        />
      ))}
      {/* Rule edges leave from the right; not a connect target, so it stays hidden. */}
      <Handle type="source" position={Position.Right} id="rules" className="cpa-handle" />
    </div>
  );
}

/**
 * A derived eligibility node.
 *
 * Drawn because the business flow chart has these, stored nowhere — it is a view of one
 * `programRules[]` entry on the owning question. Styled distinctly and marked 派生 so
 * nobody looks for a record behind it.
 */
function GrantNodeView({ data, selected }: NodeProps) {
  const { cpa } = data as unknown as QuestionData;
  return (
    <div
      className={['cpa-node', 'cpa-node--grant', selected ? 'cpa-node--selected' : '']
        .filter(Boolean)
        .join(' ')}
      style={{ width: GRANT_W }}
    >
      <Handle type="target" position={Position.Left} className="cpa-handle" />
      <div className="cpa-node__head">
        <span className="cpa-chip cpa-chip--derived">派生 · 资格</span>
      </div>
      <div className="cpa-node__title cpa-node__title--grant">{cpa.data.title}</div>
      <code className="cpa-node__expr">{cpa.data.subtitle}</code>
    </div>
  );
}

/**
 * Edge colour, as a literal rather than a CSS custom property.
 *
 * An SVG `marker` is rendered in its own <defs> subtree, where `currentColor` and the
 * edge's own class do not reach it — React Flow wants the arrow colour as a value. So
 * the stroke is set here too, keeping line and arrowhead from drifting apart.
 *
 * `#2da597` is the mock's teal. It is NOT a Cloudscape token (the nearest,
 * `color-text-status-success`, is `#00802f`) — adopted anyway because a connector is
 * not one of the component semantics Cloudscape covers, so there is no token being
 * contradicted.
 */
const FLOW_STROKE = '#2da597';
const GRANT_STROKE = '#2da597';

const nodeTypes = {
  cpaGhost: GhostNodeView,
  cpaQuestion: QuestionNodeView,
  cpaGrant: GrantNodeView,
};
// Row-skipping edges route around the cards in between (SkipEdge); conditional edges
// need a DOM label for the order badge and the raw-expression tooltip
// (ConditionEdge). Unconditional and derived edges keep the default renderer.
const edgeTypes: EdgeTypes = { cpaSkip: SkipEdge, cpaCondition: ConditionEdge };

/**
 * Unconditional edges use React Flow's `smoothstep`, i.e. orthogonal with rounded
 * corners, matching the mock — which routes every connector as right angles rather
 * than curves. Conditional and skipping edges have their own renderers and draw
 * orthogonally too, so the whole graph reads one way.
 */
const PLAIN_EDGE_TYPE = 'smoothstep';

export interface TreeCanvasProps {
  projection: Projection;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  /** Selected connection, so clicking a line can show its details. */
  selectedEdgeId?: string | null;
  /**
   * Clicking a line. Always wired now — before, a line was inert (and not even
   * clickable, since the hit area was zero) unless an editing mode was armed, so the
   * only way to read a transition was via its source node.
   */
  onSelectEdge?: (edgeId: string | null) => void;
  /** Node ids on the simulated path, when the test-path mode is on. */
  highlightNodeIds?: readonly string[];
  /**
   * Let the user drag question nodes.
   *
   * Off by default, and worth keeping off: the derived layout is what makes two
   * renders of the same rules comparable, and there is no backend field to save a
   * dragged position in — so anything moved here is browser-only and lost on refresh.
   * Exposed as a prop so a page can offer it as an explicit mode rather than a default.
   */
  draggable?: boolean;
  /** Manual positions that win over the derived ones, keyed by nodeId. */
  positionOverrides?: Record<string, { x: number; y: number }>;
  /**
   * All the cards one drop moves — the dragged one plus anything it pushed. A single
   * callback so the whole cascade is one undo step.
   */
  onNodesMoved?: (positions: Record<string, { x: number; y: number }>) => void;
  /** Clicking a `source → target` edge, used to splice a question into it. */
  onEdgeClick?: (sourceNodeId: string, targetNodeId: string) => void;
  /**
   * Let the user DRAW edges by dragging from a node's handle to another node.
   *
   * When on, handles become visible and grabbable. This is the mouse-first path for
   * wiring a freshly created card into the tree: create → configure → drag a line in.
   */
  connectable?: boolean;
  onConnectNodes?: (sourceNodeId: string, targetNodeId: string) => void;
  /**
   * Whether this pair MAY be connected, asked while the line is still being dragged.
   *
   * React Flow refuses the drop itself when this returns false, so an illegal target
   * never highlights and the gesture just does not complete. The alternative — accept
   * the drop and then explain — meant the author learned about a cycle after the edge
   * appeared, which is the worst moment for it.
   */
  canConnectNodes?: (sourceNodeId: string, targetNodeId: string) => boolean;
  /** Deleting an edge (select it, press Delete). */
  /** `ruleIndex` is 0-based and identifies WHICH rule, so parallel edges to the same
   *  target can be told apart. */
  onDisconnect?: (sourceNodeId: string, targetNodeId: string, ruleIndex?: number) => void;
  /** Pan to this node when it changes. See `FocusOnNode`. */
  focusNodeId?: string;
  /**
   * The card the editor just created. Drawn on TOP of everything and visibly marked,
   * because it is placed at the viewport centre and may land over an existing card.
   */
  newNodeId?: string;
  /** Filled with a getter for the viewport centre. See `ViewportCentre`. */
  viewportCentreRef?: MutableRefObject<(() => { x: number; y: number }) | null>;
  /** Upper zoom bound. Higher than the default when the page is all canvas. */
  maxZoom?: number;
}

/**
 * Pans the viewport to a node when `nodeId` changes.
 *
 * A child of `ReactFlowProvider` rather than part of TreeCanvas, because
 * `useReactFlow` only works INSIDE the provider — and the provider is rendered by
 * TreeCanvas itself.
 *
 * Why this exists: `fitView` runs once at mount, so a node created afterwards landed
 * wherever the layout put it — for the merged tree, the far bottom-right corner of a
 * 1894 x 1952 graph — with the viewport unmoved and nothing selected. Pressing ＋问题
 * read as the button doing nothing.
 */
function FocusOnNode({
  nodeId,
  projection,
  positionOverrides,
}: {
  nodeId?: string;
  projection: Projection;
  positionOverrides?: Record<string, { x: number; y: number }>;
}) {
  const { setCenter, getZoom } = useReactFlow();
  // The id, not the node: panning should happen when the TARGET changes, not on every
  // re-layout of the same node (dragging it would otherwise yank the viewport).
  const last = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!nodeId || nodeId === last.current) return;
    const n = projection.nodes.find((x) => x.id === nodeId);
    if (!n) return;
    last.current = nodeId;
    /*
     * The EFFECTIVE position, not the projected one.
     *
     * `projection` holds the derived layout position; a hand-placed card — dragged, or
     * dropped at the viewport centre by ＋问题 — lives in `positionOverrides`, which is
     * applied where the React Flow nodes are built. Reading the projected value here
     * panned the viewport to where the layout WOULD have parked the node (the far
     * bottom-right of the merged tree) while the card sat at the centre of the old
     * view, so the pan carried the card off screen — the node was placed correctly and
     * invisible at the same time.
     */
    const at = positionOverrides?.[nodeId] ?? n.position;
    setCenter(at.x + n.width / 2, at.y + n.height / 2, {
      // Never zoom OUT to reveal it — a user who zoomed in to work on one branch
      // should keep that zoom. Only floor it enough that the card is legible.
      zoom: Math.max(getZoom(), 0.75),
      duration: 400,
    });
  }, [nodeId, projection.nodes, positionOverrides, setCenter, getZoom]);

  return null;
}

/**
 * The drop preview: an outline of where the card will actually land.
 *
 * `resolveDrop` snaps and de-overlaps on release, so without this the card appears to
 * jump at the moment you let go. Showing the resolved slot WHILE dragging turns that
 * into feedback instead of a surprise.
 *
 * A real React Flow node rather than a hand-placed div, so the viewport transform
 * applies to it for free — a div would have to re-implement pan and zoom.
 */
function GhostNodeView({ data }: NodeProps) {
  const { w, h } = data as unknown as { w: number; h: number };
  return <div className="cpa-ghost" style={{ width: w, height: h }} />;
}

/** Short label per answer kind. Four, because the model expresses four. */
const KIND_LABEL: Record<QuestionKind, string> = {
  single: '单选',
  multi: '多选',
  number: '数值',
  text: '文本',
  custom: '组件',
};

/** Height of a freshly created card — its question text is the one-line default. */
const NEW_CARD_H = cardHeight('新问题');

/**
 * Publishes "where is the middle of what the user is looking at", in flow coordinates.
 *
 * A new detached card is placed there rather than wherever the layout would put it. On
 * the merged tree the layout parks an unconnected node in the last row at the far
 * right of a 1894 x 1952 graph, so creating one threw the viewport across the canvas —
 * the node was findable but you lost your place.
 *
 * Lives inside the provider for `useReactFlow`, and hands the page a getter rather
 * than a value because the answer is only needed at the moment of the click; tracking
 * it on every pan would re-render the page for nothing.
 */
function ViewportCentre({
  getterRef,
}: {
  getterRef?: MutableRefObject<(() => { x: number; y: number }) | null>;
}) {
  const { screenToFlowPosition } = useReactFlow();
  useEffect(() => {
    if (!getterRef) return;
    getterRef.current = () => {
      const pane = document.querySelector('.cpa-canvas .react-flow');
      const r = pane?.getBoundingClientRect();
      if (!r) return { x: 0, y: 0 };
      const c = screenToFlowPosition({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
      // A node's position is its TOP-LEFT corner, so dropping the raw centre there puts
      // the card down-and-right of the middle. Offset by half a card.
      return { x: c.x - NODE_W / 2, y: c.y - NEW_CARD_H / 2 };
    };
    return () => {
      getterRef.current = null;
    };
  }, [getterRef, screenToFlowPosition]);
  return null;
}

export default function TreeCanvas({
  projection,
  selectedNodeId,
  onSelectNode,
  selectedEdgeId,
  onSelectEdge,
  highlightNodeIds,
  draggable = false,
  positionOverrides,
  onNodesMoved,
  onEdgeClick,
  connectable = false,
  onConnectNodes,
  canConnectNodes,
  onDisconnect,
  focusNodeId,
  newNodeId,
  viewportCentreRef,
  maxZoom = 1.5,
}: TreeCanvasProps) {
  const highlight = useMemo(
    () => (highlightNodeIds ? new Set(highlightNodeIds) : null),
    [highlightNodeIds],
  );

  const severityByNode = useMemo(() => {
    const m = new Map<string, 'error' | 'warning'>();
    for (const i of projection.issues) {
      if (!i.nodeId) continue;
      // `info` means "checked, undecidable" — nothing is known to be wrong, so it must
      // not put a defect stripe on a card.
      if (i.severity === 'info') continue;
      // Error wins: a node with both should read as broken, not as a nit.
      if (i.severity === 'error' || !m.has(i.nodeId)) m.set(i.nodeId, i.severity);
    }
    return m;
  }, [projection.issues]);

  /** The drop preview while a card is being dragged. See `GhostNodeView`. */
  const [ghost, setGhost] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
    pushed: { id: string; x: number; y: number }[];
  } | null>(null);

  /**
   * Which port each edge leaves from, and how many ports its source needs.
   *
   * Ordered by the projection's own edge order, which is rule order — so port 0 is the
   * first `nextQuestionRules` entry, matching the ①②③ badges on the labels. Left to
   * right on the card therefore reads as evaluation order.
   */
  const ports = useMemo(() => {
    const bySource = new Map<string, string[]>();
    for (const e of projection.edges) {
      if (e.kind === 'grant') continue; // grants use their own right-hand port
      if (!bySource.has(e.source)) bySource.set(e.source, []);
      bySource.get(e.source)!.push(e.id);
    }
    const slotOf = new Map<string, number>();
    const countOf = new Map<string, number>();
    for (const [src, ids] of bySource) {
      countOf.set(src, ids.length);
      ids.forEach((id, i) => slotOf.set(id, i));
    }
    return { slotOf, countOf };
  }, [projection.edges]);

  const nodes: Node[] = useMemo(
    () =>
      projection.nodes.map((n) => ({
        id: n.id,
        type: n.kind === 'question' ? 'cpaQuestion' : 'cpaGrant',
        position: positionOverrides?.[n.id] ?? n.position,
        selected: n.id === selectedNodeId,
        // Derived nodes stay pinned even in free mode: they are a view of a rule on
        // their owner, so moving one away from its owner would say something false.
        draggable: draggable && n.kind === 'question',
        // Dim everything off the simulated path rather than hiding it: the shape of
        // the tree is the context that makes a path meaningful.
        style: highlight && !highlight.has(n.data.ownerNodeId) ? { opacity: 0.25 } : undefined,
        // Above every other card. It is dropped at the viewport centre, so it can land
        // on top of one — and the one you just made is the one you need to see.
        zIndex: n.id === newNodeId ? 1000 : undefined,
        data: {
          cpa: n,
          isEntry: n.id === projection.entryNodeId,
          worstSeverity: severityByNode.get(n.id) ?? null,
          connectable: connectable && n.kind === 'question',
          isNew: n.id === newNodeId,
          outCount: ports.countOf.get(n.id) ?? 0,
        } satisfies QuestionData as unknown as Record<string, unknown>,
      })),
    [
      projection,
      selectedNodeId,
      highlight,
      severityByNode,
      draggable,
      positionOverrides,
      connectable,
      newNodeId,
      ports,
    ],
  );

  /*
   * The ghost is appended rather than mixed into the memo above, so a drag does not
   * rebuild every card's props on every probe.
   */
  const nodesWithGhost: Node[] = useMemo(
    () =>
      ghost
        ? [
            // Cards about to be pushed are shown at their NEW slot while dragging, so
            // the cascade is visible before it is committed rather than after.
            ...nodes.map((n) => {
              const p = ghost.pushed.find((q) => q.id === n.id);
              return p ? { ...n, position: { x: p.x, y: p.y } } : n;
            }),
            {
              id: '__cpa_ghost__',
              type: 'cpaGhost',
              position: { x: ghost.x, y: ghost.y },
              data: { w: ghost.w, h: ghost.h },
              draggable: false,
              selectable: false,
              // Under the card being dragged, over everything else.
              zIndex: 900,
            } as Node,
          ]
        : nodes,
    [nodes, ghost],
  );

  const edges: Edge[] = useMemo(
    () =>
      projection.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        /*
         * A lane is only assigned to an edge that jumps a row, and such an edge MUST
         * be routed or it ends up hidden behind the cards it passes.
         *
         * `always` edges use the labelled renderer too, not React Flow's default:
         * they now carry 「无条件」, and the default renderer draws no DOM label, so
         * leaving them on it would silently swallow that text. Only grant edges stay
         * on the default — they are the one kind with nothing to say on the line.
         */
        type:
          e.lane !== undefined
            ? 'cpaSkip'
            : e.kind === 'grant'
              ? PLAIN_EDGE_TYPE
              : 'cpaCondition',
        // Both custom renderers read the projected edge straight off `data`, so the
        // label logic lives in one place (EdgeLabel) rather than in each renderer.
        data: { laneX: e.laneX, edge: e },
        sourceHandle: e.kind === 'grant' ? 'rules' : `s-${ports.slotOf.get(e.id) ?? 0}`,
        animated: false,
        className: `cpa-edge cpa-edge--${e.kind}`,
        style:
          e.kind === 'grant'
            ? { strokeDasharray: '2 3', strokeWidth: 1.2, stroke: GRANT_STROKE }
            : {
                strokeWidth: 1.6,
                stroke: FLOW_STROKE,
                // The mock draws its one row-skipping connector DASHED, which is the
                // same reading we arrived at independently: a detoured line is not
                // the same kind of thing as a straight one. Conditional edges stay
                // solid — the mock's are solid and the condition is on the label.
                strokeDasharray: e.lane !== undefined ? '7 5' : undefined,
                // A branch that can never be taken should not read as a live path.
                opacity: e.isDeadBranch ? 0.35 : 1,
              },
        // Direction is in the data (source -> target) and the layout reads top-down,
        // but neither is visible on the line itself: React Flow draws no arrowhead
        // unless asked. Without this a reader cannot tell a branch from a merge, and
        // in connect mode cannot tell which end they just wired.
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 16,
          height: 16,
          color: e.kind === 'grant' ? GRANT_STROKE : FLOW_STROKE,
        },
        // A 1.6px line is near-impossible to hit, and now a click always does
        // something (select + inspect), so every non-derived edge gets the wide
        // invisible hit area.
        interactionWidth: e.kind !== 'grant' ? 20 : 0,
        selected: e.id === selectedEdgeId,
        // A derived rule edge has no stored counterpart, so it can never be deleted.
        deletable: !!onDisconnect && e.kind !== 'grant',
        selectable: e.kind !== 'grant',
      })),
    [projection.edges, onEdgeClick, onDisconnect, selectedEdgeId],
  );

  /**
   * Selection comes from the CLICK, not from React Flow's selection store.
   *
   * This used to be `onSelectionChange`, which is a subscription to that store rather
   * than an event: it is invoked out of band with the render that caused it, and the
   * card's own `selected` flag is controlled from our state. The two chased each other
   * by one tick — clicking a card appeared to do nothing, and clicking the next one
   * showed the PREVIOUS card's details.
   *
   * `onNodeClick` hands us the node that was clicked, synchronously, with no store in
   * between. `onPaneClick` already covers clearing the selection, which was the only
   * other thing the subscription was doing.
   */
  const handleNodeClick = useCallback(
    (_e: unknown, node: Node) => {
      if (node.type === 'cpaGhost') return;
      onSelectEdge?.(null);
      onSelectNode(node.id);
    },
    [onSelectNode, onSelectEdge],
  );

  /**
   * Where a card would land if released now — snapped to the lattice and off anything
   * it would cover (`resolveDrop`).
   *
   * Fed the RENDERED rects (position overrides applied), not the projected ones,
   * because that is what the user sees and aims at.
   */
  const resolveFor = useCallback(
    (node: Node) => {
      const rects = nodes
        .filter((n) => n.type !== 'cpaGhost')
        .map((n) => {
          const cpa = (n.data as unknown as QuestionData).cpa;
          return {
            id: n.id,
            x: n.position.x,
            y: n.position.y,
            w: cpa.width,
            h: cpa.height,
          };
        });
      const self = rects.find((r) => r.id === node.id);
      const w = self?.w ?? NODE_W;
      const h = self?.h ?? NEW_CARD_H;
      const plan = planDrop({
        id: node.id,
        at: node.position,
        rects,
        colPitch: NODE_W + COL_GAP,
        rowPitch: NODE_H + ROW_GAP,
      });
      return { ...plan, w, h };
    },
    [nodes],
  );

  /*
   * Recomputed only when the pointer has actually moved a few pixels.
   *
   * `onNodeDrag` fires on every mouse move, and when the snapped slot is occupied
   * `resolveDrop` scans the whole lattice — that is fine a few times per drag and far
   * too much at 60fps. The threshold is well under one card, so the preview still
   * tracks the pointer closely.
   */
  const lastProbe = useRef<{ x: number; y: number } | null>(null);
  /**
   * Where the gesture started, and whether it has travelled far enough to be a drag.
   *
   * React Flow raises `onNodeDrag` and `onNodeDragStop` for ANY mousedown→mouseup on a
   * node, including a zero-distance click. Without this guard a plain click ran the
   * whole drop path: it showed a ghost, pinned the card, could shove its neighbours,
   * and dispatched `set-positions` — and that burst of re-renders landed on top of
   * React Flow's async `onSelectionChange`, so the first click appeared to do nothing
   * and the next one selected the PREVIOUS card.
   *
   * A click must never re-place a node. 4px is below the noise floor of a deliberate
   * click and far below a real drag.
   */
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const DRAG_THRESHOLD = 4;

  const handleDragStart = useCallback((_e: unknown, node: Node) => {
    dragStart.current = { x: node.position.x, y: node.position.y };
    lastProbe.current = null;
  }, []);

  /** Has the pointer moved far enough from the gesture's start to count as a drag? */
  const isDrag = (node: Node) => {
    const s0 = dragStart.current;
    if (!s0) return false;
    return (
      Math.abs(node.position.x - s0.x) >= DRAG_THRESHOLD ||
      Math.abs(node.position.y - s0.y) >= DRAG_THRESHOLD
    );
  };

  const handleDrag = useCallback(
    (_e: unknown, node: Node) => {
      if (!isDrag(node)) return;
      const p = lastProbe.current;
      if (p && Math.abs(p.x - node.position.x) < 6 && Math.abs(p.y - node.position.y) < 6) {
        return;
      }
      lastProbe.current = { x: node.position.x, y: node.position.y };
      const r = resolveFor(node);
      setGhost({ x: r.at.x, y: r.at.y, w: r.w, h: r.h, pushed: r.pushed });
    },
    [resolveFor],
  );

  const handleDragStop = useCallback(
    (_e: unknown, node: Node) => {
      const moved = isDrag(node);
      dragStart.current = null;
      lastProbe.current = null;
      setGhost(null);
      // A click: let selection happen and change nothing else.
      if (!moved || !onNodesMoved) return;
      const r = resolveFor(node);
      onNodesMoved({
        [node.id]: r.at,
        ...Object.fromEntries(r.pushed.map((p) => [p.id, { x: p.x, y: p.y }])),
      });
    },
    [onNodesMoved, resolveFor],
  );

  const handleConnect = useCallback(
    (c: { source?: string | null; target?: string | null }) => {
      if (!c.source || !c.target) return;
      onConnectNodes?.(c.source, c.target);
    },
    [onConnectNodes],
  );

  const isValidConnection = useCallback(
    (c: { source?: string | null; target?: string | null }) => {
      if (!c.source || !c.target || c.source === c.target) return false;
      // No predicate means "anything the base rules allow" — the default React Flow
      // would apply anyway, so the prop stays optional.
      return canConnectNodes ? canConnectNodes(c.source, c.target) : true;
    },
    [canConnectNodes],
  );

  const handleEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      for (const e of deleted) {
        const projected = projection.edges.find((x) => x.id === e.id);
        if (projected && projected.kind !== 'grant') {
          onDisconnect?.(
            projected.source,
            projected.target,
            // CpaEdge.ruleIndex is 1-based for display; the model wants 0-based.
            projected.ruleIndex === undefined ? undefined : projected.ruleIndex - 1,
          );
        }
      }
    },
    [onDisconnect, projection.edges],
  );

  const handleEdgeClick = useCallback(
    (_e: unknown, edge: Edge) => {
      const projected = projection.edges.find((x) => x.id === edge.id);
      if (!projected) return;
      // Selecting works for every edge, derived ones included — reading a rule
      // relation is useful even though it cannot be edited.
      onSelectEdge?.(projected.id);
      onSelectNode(null);
      // Splitting, on the other hand, needs a real stored edge.
      if (projected.kind === 'grant') return;
      onEdgeClick?.(projected.source, projected.target);
    },
    [onEdgeClick, onSelectEdge, onSelectNode, projection.edges],
  );

  return (
    <div className={`cpa-canvas ${connectable ? 'cpa-canvas--connect' : ''}`}>
      <ReactFlowProvider>
        <FocusOnNode
          nodeId={focusNodeId}
          projection={projection}
          positionOverrides={positionOverrides}
        />
        <ViewportCentre getterRef={viewportCentreRef} />
        <ReactFlow
          nodes={nodesWithGhost}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onPaneClick={() => {
            onSelectNode(null);
            onSelectEdge?.(null);
          }}
          onNodeDragStart={draggable ? handleDragStart : undefined}
          onNodeDrag={draggable ? handleDrag : undefined}
          onNodeDragStop={draggable ? handleDragStop : undefined}
          onEdgeClick={handleEdgeClick}
          onConnect={connectable ? handleConnect : undefined}
          isValidConnection={connectable ? isValidConnection : undefined}
          onEdgesDelete={onDisconnect ? handleEdgesDelete : undefined}
          nodesDraggable={draggable}
          nodesConnectable={connectable}
          // Node deletion goes through the page's impact dialog, never the Delete key:
          // removing a question silently breaks every rule that reads it.
          deleteKeyCode={onDisconnect ? ['Delete', 'Backspace'] : null}
          nodesFocusable={false}
          elementsSelectable
          fitView
          /*
           * fitView is allowed to ACTUALLY fit.
           *
           * There used to be a `minZoom: 0.55` floor here, added when the canvas showed
           * q_1_1 alone — 1176 x 1168, near square, and a plain fit scaled it to the
           * pane's short dimension until the text was unreadable. The merged `q_all` is
           * 2594 x 1352: fitting it needs ~0.44, so that floor silently CROPPED the
           * graph instead of scaling it, and the two right-hand subtrees (Q1.4.1,
           * Q1.5.1) opened off-screen — the whole point of merging was to see them.
           *
           * A floor cannot be right for both shapes, and cropping is the worse failure:
           * a small-but-whole tree tells you what you are looking at and the zoom
           * controls fix it, while a cropped one looks like missing data. `0.2` is a
           * sanity bound, not a readability target.
           */
          fitViewOptions={{ padding: 0.08, minZoom: 0.2, maxZoom: 1 }}
          minZoom={0.1}
          maxZoom={maxZoom}
          proOptions={{ hideAttribution: false }}
        >
          <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
          <Controls showInteractive={false} />
          {/* No minimap. The mock has none, and on a 15-row spine it rendered as a
              large near-empty grey rectangle covering the bottom-right of the canvas
              — it cost real space and showed nothing the viewport did not. */}
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
